// various util functions for dom manipulation and traversal
export function traverseChildren(node, callback) {
  callback(node);
  if (node.children) {
    for (let i = 0; i < node.children.length; i++) {
      traverseChildren(node.children[i], callback);
    }
  }
}

export function traverseChildNodes(node, callback) {
  callback(node);
  if (node.childNodes.length > 0) {
    for (let i = 0; i < node.childNodes.length; i++) {
      traverseChildNodes(node.childNodes[i], callback);
    }
  }
}

export function findNode(node, callback) {
  let foundNode;
  traverseChildren(node, n => {
    if (callback(n)) {
      foundNode = n;
    }
  });
  return foundNode;
}

export function stripHTMLWhiteSpace(str) {
  str = str.replace(/(\r\n|\n|\r)/gm, '');
  str = str.replace(/>\s+</g,'><');
  str = str.replace(/>\s+/g, '> ');
  str = str.replace(/\s+</g, ' <');
  return str;
}

export function findNodeFromRangeText(containerNode, text) {
  const elements = [];
  traverseChildNodes(containerNode, node => {
    if (node.textContent === text) {
      elements.push(node);
    }
  });

  if (elements.length === 0) {
    return null;
  }
  // return last element
  return elements[elements.length - 1];
}

export function getTextNodesBetween(rootNode, startNode, endNode) {
  var pastStartNode = false, reachedEndNode = false, textNodes = [];
  if (startNode === endNode) {
    return [];
  }

  traverseChildNodes(rootNode, function (node) {
    const isStart = node === startNode;
    const isEnd = node === endNode;
    const isText = node.nodeType === 3;

    if  (isStart) {
      pastStartNode = true;
    } else if (pastStartNode && isText && !isEnd && !reachedEndNode) {
      textNodes.push(node);
    } else if (isEnd) {
      reachedEndNode = true;
    }
  });

  return textNodes;
}

export function highlightSelection(containerElement, {
  startContainerText,
  endContainerText,
  startOffset,
  endOffset,
}) {
  const startNode = findNodeFromRangeText(containerElement, startContainerText);
  const endNode = findNodeFromRangeText(containerElement, endContainerText);

  if (startNode && startNode === endNode) {
    const beforeText = startNode.textContent.substring(0, startOffset);
    const highlightedText = startNode.textContent.substring(startOffset, endOffset);
    const afterText = startNode.textContent.substring(endOffset);

    const mark = document.createElement('mark');
    mark.appendChild(document.createTextNode(highlightedText));

    const parent = startNode.parentNode;

    parent.replaceChild(mark, startNode);
    parent.insertBefore(document.createTextNode(beforeText), mark);
    parent.insertBefore(document.createTextNode(afterText), mark.nextSibling);
  } else {

    // First highlight the nodes between the start and end nodes
    {
      const textNodesBetween = getTextNodesBetween(containerElement, startNode, endNode);

      textNodesBetween.forEach(node => {
        const mark = document.createElement('mark');
        mark.appendChild(document.createTextNode(node.textContent));
        node.parentNode.replaceChild(mark, node);
      });
    }

    // Then highlight the start node
    if (startNode) {
      const beforeHighlight = startNode.textContent.substring(0, startOffset);
      const highlightedText = startNode.textContent.substring(startOffset);

      const mark = document.createElement('mark');
      mark.appendChild(document.createTextNode(highlightedText));
      const parent = startNode.parentNode;

      parent.replaceChild(mark, startNode);
      parent.insertBefore(document.createTextNode(beforeHighlight), mark);
    }

    // Then highlight the end node
    if (endNode) {
      const highlightedText = endNode.textContent.substring(0, endOffset);
      const afterText = endNode.textContent.substring(endOffset);

      const mark = document.createElement('mark');
      mark.appendChild(document.createTextNode(highlightedText));
      const parent = endNode.parentNode;

      parent.replaceChild(mark, endNode);
      parent.insertBefore(document.createTextNode(afterText), mark.nextSibling);
    }
  }
  return containerElement.innerHTML;
}

export function intersectsHighlight(containerElement, {
  startContainerText,
  endContainerText,
}) {
  const startNode = findNodeFromRangeText(containerElement, startContainerText);
  const endNode = findNodeFromRangeText(containerElement, endContainerText);

  // Is the startNode inside a highlight?
  if (startNode.parentNode?.nodeName === 'MARK') {
    return true;
  }

  // Is the endNode inside a highlight?
  if (endNode.parentNode?.nodeName === 'MARK') {
    return true;
  }

  // Is one of the nodes between the start and end node inside a highlight?
  const textNodesBetween = getTextNodesBetween(containerElement, startNode, endNode);
  for (let i = 0; i < textNodesBetween.length; i++) {
    if (textNodesBetween[i].parentNode?.nodeName === 'MARK') {
      return true;
    }
  }

  return false;
}

export function isAllHighlighted(containerElement, {
  startContainerText,
  endContainerText,
}) {
  const startNode = findNodeFromRangeText(containerElement, startContainerText);
  const endNode = findNodeFromRangeText(containerElement, endContainerText);

  let allHighlighted = true;

  // Is the startNode inside a highlight?
  if (startNode?.parentNode?.nodeName !== 'MARK') {
    allHighlighted = false;
  }

  // Is the endNode inside a highlight?
  if (endNode?.parentNode?.nodeName !== 'MARK') {
    allHighlighted = false;
  }

  // Are any of the nodes in between not in a highlight?
  const textNodesBetween = getTextNodesBetween(containerElement, startNode, endNode);
  for (let i = 0; i < textNodesBetween.length; i++) {
    if (textNodesBetween[i] && textNodesBetween[i].parentNode?.nodeName !== 'MARK') {
      allHighlighted = false;
    }
  }

  return allHighlighted;
}

export function removeHighlight(containerElement, {
  startContainerText,
  endContainerText,
  startOffset,
  endOffset,
}) {
  const startNode = findNodeFromRangeText(containerElement, startContainerText);
  const endNode = findNodeFromRangeText(containerElement, endContainerText);

  if (startNode === endNode) {
    const selectedText = startNode.textContent.substring(startOffset, endOffset);

    if (selectedText === startNode.textContent) {
      // selected text is the full text of the node
      const textNode = document.createTextNode(selectedText);
      startNode.parentNode.parentNode.replaceChild(textNode, startNode.parentNode);
    } else {
      const beforeText = startNode.textContent.substring(0, startOffset);
      const afterText = startNode.textContent.substring(endOffset);

      if (afterText && !beforeText) {
        const mark = document.createElement('mark');
        mark.appendChild(document.createTextNode(afterText));
        startNode.parentNode.parentNode.insertBefore(mark, startNode.parentNode.nextSibling);
        const selectedTextNode = document.createTextNode(selectedText);
        startNode.parentNode.parentNode.replaceChild(selectedTextNode, startNode.parentNode);
      }

      if (beforeText && !afterText) {
        const selectedTextNode = document.createTextNode(selectedText);
        startNode.parentNode.parentNode.insertBefore(selectedTextNode, startNode.parentNode.nextSibling);
        const mark = document.createElement('mark');
        mark.appendChild(document.createTextNode(beforeText));
        startNode.parentNode.parentNode.replaceChild(mark, startNode.parentNode);
      }

      if (beforeText && afterText) {
        const mark = document.createElement('mark');
        mark.appendChild(document.createTextNode(afterText));
        startNode.parentNode.parentNode.insertBefore(mark, startNode.parentNode.nextSibling);
        const selectedTextNode = document.createTextNode(selectedText);
        startNode.parentNode.parentNode.insertBefore(selectedTextNode, mark);
        const mark2 = document.createElement('mark');
        mark2.appendChild(document.createTextNode(beforeText));
        startNode.parentNode.parentNode.replaceChild(mark2, startNode.parentNode);
      }
    }
  } else {
    {
      // First convert the marked nodes in between the start and end nodes to text nodes
      const textNodesBetween = getTextNodesBetween(containerElement, startNode, endNode);
      for (let i = 0; i < textNodesBetween.length; i++) {
        if (textNodesBetween[i] && textNodesBetween[i].parentNode?.nodeName === 'MARK') {
          textNodesBetween[i].parentNode.parentNode.replaceChild(textNodesBetween[i], textNodesBetween[i].parentNode);
        }
      }

      // Then alter the node at the start of the selection
      startNode.parentNode.parentNode.innerHTML = removeHighlight(startNode.parentNode.parentNode, {
        startContainerText,
        endContainerText: startContainerText,
        startOffset,
        endOffset: startContainerText.length,
      });

      // Then alter the node at the end of the selection
      endNode.parentNode.innerHTML = removeHighlight(containerElement, {
        startContainerText: endContainerText,
        endContainerText,
        startOffset: 0,
        endOffset,
      });
    }
  }
  return containerElement.innerHTML;
};

export function removeRedundantMarks(html) {
  html = html.replace(/<\/mark> <mark>/g, ' ').replace(/<\/mark><mark>/g, '');;

  // make virtual DOM
  const virtualDOM = document.createElement('div');
  virtualDOM.innerHTML = html;

  // if any parent of a mark is a mark, convert it to a text node
  const marks = virtualDOM.querySelectorAll('mark');
  for (let i = 0; i < marks.length; i++) {
    if (marks[i].parentNode?.nodeName === 'MARK') {
      const textNode = document.createTextNode(marks[i].textContent);
      marks[i].parentNode.replaceChild(textNode, marks[i]);
    }
  }

  return virtualDOM.innerHTML;
}
